import {
  Building2,
  Clock,
  Lightbulb,
  ListChecks,
  Settings2,
  Sparkles
} from "lucide-react";
import { useMemo, type FC, type MouseEvent } from "react";
import { toast } from "sonner";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";

import { artifactKey, useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { PaneMaximizeButton } from "../layout/PaneMaximizeButton";
import { toggleFocusFromHeader } from "../layout/paneShortcuts";
import type { Artifact } from "../types";
import { shareableLink } from "../urlState";
import { iconFileForArtifact } from "../workspace/iconFile";
import { TabCloseButton } from "./TabCloseButton";

const TabIcon: FC<{
  artifact: Artifact;
  file?: {
    name: string;
    filename?: string;
    mime_type?: string | null;
    kind?: string;
  } | null;
}> = ({ artifact, file }) => {
  const iconFile = iconFileForArtifact(artifact, file);
  if (iconFile) {
    return (
      <FileTypeIcon
        name={iconFile.name}
        mimeType={iconFile.mimeType}
        isFolder={iconFile.isFolder}
        size={14}
      />
    );
  }
  const Glyph =
    artifact.kind === "settings"
      ? Settings2
      : artifact.kind === "key_facts"
      ? Lightbulb
      : artifact.kind === "time"
      ? Clock
      : artifact.kind === "company"
      ? Building2
      : artifact.kind === "workflows" || artifact.kind === "card"
      ? Sparkles
      : ListChecks;
  return <Glyph className="size-3.5 shrink-0 text-muted-foreground/80" />;
};

const Tab: FC<{
  artifact: Artifact;
  active: boolean;
  file?: { name: string; mime_type?: string | null; kind?: string } | null;
}> = ({ artifact, active, file }) => {
  const { setActive, close, closeOtherTabs } = useCaseIde();
  const key = artifactKey(artifact);

  const onMiddleClick = (e: MouseEvent) => {
    if (e.button === 1) {
      e.preventDefault();
      close(key);
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          role="tab"
          aria-selected={active}
          tabIndex={0}
          title={artifact.title}
          onClick={() => setActive(key)}
          onAuxClick={onMiddleClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setActive(key);
          }}
          className={cn(
            "group flex h-8 max-w-56 min-w-0 shrink-0 cursor-pointer items-center gap-1.5 border-r border-border py-0 pr-1.5 pl-2.5 text-xs select-none",
            active
              ? "bg-background text-foreground shadow-[inset_0_-2px_0_0_var(--color-primary)]"
              : "bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
        >
          <TabIcon artifact={artifact} file={file} />
          <span className="truncate">{artifact.title}</span>
          <TabCloseButton
            label={`Close ${artifact.title}`}
            active={active}
            onClick={() => close(key)}
          />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={() => close(key)}>Close</ContextMenuItem>
        <ContextMenuItem onSelect={() => closeOtherTabs(key)}>
          Close others
        </ContextMenuItem>
        <ContextMenuItem
          onSelect={() =>
            void navigator.clipboard
              ?.writeText(shareableLink(artifact))
              .then(() => toast.success("Link copied"))
              .catch(() => toast.error("Couldn't copy the link"))
          }
        >
          Copy Link
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

/**
 * The open-artifact tab strip, ending in the document's Maximize control
 * (double-clicking the strip does the same). Case-level actions live in the
 * case header.
 */
export const ArtifactTabs: FC = () => {
  const { tabs, activeKey } = useCaseIde();
  const { fs } = useCaseData();
  const filesById = useMemo(
    () => new Map(fs.nodes.map((n) => [n.id, n])),
    [fs.nodes]
  );
  return (
    <div
      className="artifact-tabs flex h-8 w-full shrink-0 items-stretch border-b border-border bg-muted/40"
      onDoubleClick={(e) => toggleFocusFromHeader(e, "document")}
    >
      <div
        role="tablist"
        className="flex min-w-0 flex-1 items-stretch overflow-x-auto [scrollbar-width:thin]"
      >
        {tabs.map((tab) => {
          const key = artifactKey(tab);
          return (
            <Tab
              key={key}
              artifact={tab}
              active={key === activeKey}
              file={tab.kind === "file" ? filesById.get(tab.id) : undefined}
            />
          );
        })}
      </div>
      <div className="flex shrink-0 items-center px-1">
        <PaneMaximizeButton focus="document" />
      </div>
    </div>
  );
};
